import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Film, Loader2, LogOut, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader, useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";

const emptyForm = { title: "", caption: "", category: "", published: true, sort_order: 0, playback_rate: 1.5 };

export default function AdminReels() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [videoFile, setVideoFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reels", { headers: authHeader() });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Could not load reels.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (path, file) => {
    const body = new FormData();
    body.append("file", file);
    const { data } = await api.post(path, body, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !videoFile) {
      toast.error("Reel title and video are required.");
      return;
    }
    setSaving(true);
    try {
      const video = await upload("/admin/media/upload-video", videoFile);
      const poster = posterFile ? await upload("/admin/media/upload-image", posterFile) : null;
      await api.post("/admin/reels", {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        playback_rate: Number(form.playback_rate) || 1.5,
        video,
        poster,
      }, { headers: authHeader() });
      toast.success("Reel added.");
      setForm(emptyForm);
      setVideoFile(null);
      setPosterFile(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video upload failed.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (item) => {
    try {
      await api.put(`/admin/reels/${item.id}`, {
        title: item.title,
        caption: item.caption || null,
        category: item.category || null,
        video: item.video,
        poster: item.poster || null,
        published: !item.published,
        sort_order: item.sort_order || 0,
        playback_rate: item.playback_rate || 1.5,
      }, { headers: authHeader() });
      await load();
    } catch {
      toast.error("Could not update reel.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete reel “${item.title}”?`)) return;
    try {
      await api.delete(`/admin/reels/${item.id}`, { headers: authHeader() });
      toast.success("Reel deleted.");
      await load();
    } catch {
      toast.error("Could not delete reel.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-black paper-grain">
      <header className="sticky top-0 z-40 bg-[#F4F4F0]/95 backdrop-blur border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO.mark} alt="Arvind Printers" className="h-10 w-auto" />
            <div className="hidden sm:block"><div className="font-display text-lg leading-none">Arvind Printers</div><div className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/60 mt-1">Admin · Reels</div></div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/admin/quotes" className="btn-offset">Quotes</Link>
            <Link to="/admin/portfolio" className="btn-offset">Portfolio</Link>
            <span className="hidden lg:block font-mono text-[10px] tracking-[0.18em] uppercase text-black/60">{user?.email}</span>
            <button className="btn-offset" onClick={() => { logout(); navigate("/admin/login", { replace: true }); }}><LogOut className="w-3.5 h-3.5" /> Log out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10"><div className="font-mono text-xs tracking-[0.24em] uppercase text-black/60">Media Centre / Motion</div><h1 className="font-display text-5xl md:text-6xl leading-none mt-2">Reels Manager</h1><p className="mt-4 max-w-2xl text-black/70">Upload short vertical product and production videos. We’ll show the published ones between Catalog and Infrastructure.</p></div>

        <div className="grid lg:grid-cols-[430px_1fr] gap-8 items-start">
          <form onSubmit={submit} className="border border-black p-5 md:p-6 bg-white/35 lg:sticky lg:top-28">
            <div className="font-mono text-xs tracking-[0.2em] uppercase mb-5">Add reel</div>
            <div className="space-y-4">
              <Field label="Reel title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Foil stamping in motion" />
              <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="Packaging / Offset / DTF" />
              <label className="block"><span className="label">Caption</span><textarea className="input min-h-24" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="One-line explanation for clients…" /></label>
              <label className="block"><span className="label">Video</span><input className="input py-2" type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} /><small className="block mt-1 text-black/55">Best: vertical 9:16, short MP4, under 100 MB.</small></label>
              <label className="block"><span className="label">Poster image (optional)</span><input className="input py-2" type="file" accept="image/*" onChange={(e) => setPosterFile(e.target.files?.[0] || null)} /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="label">Display order</span><input className="input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></label>
                <label className="block"><span className="label">Speed</span><select className="input" value={form.playback_rate} onChange={(e) => setForm({ ...form, playback_rate: e.target.value })}><option value="1">1×</option><option value="1.25">1.25×</option><option value="1.5">1.5×</option><option value="1.75">1.75×</option><option value="2">2×</option></select></label>
              </div>
              <label className="flex items-center gap-2 font-mono text-xs uppercase"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publish now</label>
              <button disabled={saving} className="btn-offset w-full justify-center py-3">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading video…</> : <><Upload className="w-4 h-4" /> Upload reel</>}</button>
            </div>
          </form>

          <section>
            <div className="flex items-center justify-between border-b border-black pb-3 mb-4"><div className="font-mono text-xs tracking-[0.2em] uppercase">Existing reels</div><div className="font-mono text-xs text-black/55">{items.length} total</div></div>
            {loading ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin" /></div> : items.length === 0 ? <div className="border border-dashed border-black/40 py-24 text-center"><Film className="mx-auto mb-3" /><p>No reels yet. Upload your first product video.</p></div> : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((item) => <article key={item.id} className="border border-black bg-white/25 overflow-hidden">
                  <div className="aspect-[9/16] bg-black overflow-hidden"><video src={item.video?.url} poster={item.poster?.url || undefined} muted loop playsInline controls className="w-full h-full object-cover" /></div>
                  <div className="p-4"><div className="font-mono text-[11px] tracking-[0.15em] uppercase text-black/55">{item.category || "Reel"} · {item.playback_rate || 1.5}×</div><h2 className="font-display text-2xl mt-1">{item.title}</h2><p className="text-sm text-black/65 mt-2 line-clamp-2">{item.caption || "No caption"}</p><div className="flex gap-2 mt-4"><button className="btn-offset flex-1 justify-center" onClick={() => togglePublished(item)}>{item.published ? "Published" : "Hidden"}</button><button className="btn-offset" onClick={() => remove(item)} aria-label="Delete reel"><Trash2 className="w-4 h-4" /></button></div></div>
                </article>)}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return <label className="block"><span className="label">{label}</span><input className="input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} /></label>;
}
